import DialogContent from '@mui/material/DialogContent'
import { styled } from '@mui/material/styles'
import React from 'react'

import Dialog from './dialog'
import DialogHeader from './dialog-header'
import { getGroupTitle, PzMetadata } from './pz-metadata'

type Props = {
  details?: PzMetadata
  expression?: { text: string; pipelineStages: string[] }
  trigger: HTMLElement | null
  getFallbackFocus: () => HTMLElement | undefined
  onClose: () => void
}

// Explains a personalization's source and field reference without changing its value.
const PzDetailsDialog: React.FC<Props> = ({ details, expression, trigger, getFallbackFocus, onClose }) => {
  const titleId = React.useId()
  const lastDetails = React.useRef({ details, expression })
  if (details) lastDetails.current = { details, expression }
  const { details: content, expression: currentExpression } = lastDetails.current
  const hasPipeline = Boolean(currentExpression?.pipelineStages.length)

  return (
    <Dialog
      aria-labelledby={titleId}
      disableRestoreFocus
      fullWidth
      maxWidth="sm"
      open={Boolean(details)}
      slotProps={{
        transition: {
          onEntered: (element) => {
            if (!element.contains(document.activeElement)) {
              element.focus({ preventScroll: true })
            }
          },
          onExited: () => {
            const target = trigger?.isConnected ? trigger : getFallbackFocus()
            target?.focus({ preventScroll: true })
          },
        },
      }}
      onClose={onClose}
    >
      <DialogHeader
        id={`${titleId}-header`}
        title={<span id={titleId}>Understanding personalization</span>}
        onClose={onClose}
      />
      <Content>
        {content && (
          <>
            <Explanation>
              This field inserts personalized data, meaning its value can vary by contact or based on the field’s
              source. The data could come from a contact field, an incoming message, an answer to a previous question,
              an API response, or another source. Below, you can see where the inserted data comes from.
            </Explanation>
            <Section as="dl">
              <SectionLabel as="dt">Where it comes from</SectionLabel>
              <SourceValue>
                <span>{getGroupTitle(content.group)}</span> <span aria-hidden="true">→</span> {content.label}
              </SourceValue>
            </Section>
            {hasPipeline && (
              <Section aria-labelledby={`${titleId}-pipeline`}>
                <SectionLabel id={`${titleId}-pipeline`}>Modifiers</SectionLabel>
                <PipelineDescription>These modify the field’s value in order, from top to bottom.</PipelineDescription>
                <PipelineStages>
                  {currentExpression?.pipelineStages.map((stage, index) => (
                    <li key={`${index}:${stage}`}>
                      <code>{stage}</code>
                    </li>
                  ))}
                </PipelineStages>
              </Section>
            )}
            <Reference key={`${content.group}:${currentExpression?.text || content.value}`}>
              <summary>{hasPipeline ? 'Full expression' : 'Field reference'}</summary>
              <code>{currentExpression?.text ?? `{{${content.value}}}`}</code>
            </Reference>
          </>
        )}
      </Content>
    </Dialog>
  )
}

const Content = styled(DialogContent)(({ theme }) => ({
  paddingTop: `${theme.spacing(3)} !important`,
}))

const SectionLabel = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightMedium,
}))

const Explanation = styled('p')(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.primary,
  margin: theme.spacing(0, 0, 3),
}))

const Section = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0, 0, 2),
  padding: theme.spacing(2),
}))

const SourceValue = styled('dd')(({ theme }) => ({
  ...theme.typography.body1,
  margin: theme.spacing(1, 0, 0),
  overflowWrap: 'anywhere',
}))

const PipelineStages = styled('ol')(({ theme }) => ({
  ...theme.typography.body2,
  margin: theme.spacing(1.5, 0, 0),
  paddingLeft: theme.spacing(3),
  '& li': {
    padding: theme.spacing(1, 0, 1, 0.5),
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    '&::marker': {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightMedium,
    },
    '& + li': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
}))

const PipelineDescription = styled('p')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  margin: theme.spacing(1, 0, 0),
}))

const Reference = styled('details')(({ theme }) => ({
  '& summary': {
    ...theme.typography.body2,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    width: 'fit-content',
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 4 },
  },
  '& code': {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'block',
    marginTop: theme.spacing(1.5),
    overflowWrap: 'anywhere',
    padding: theme.spacing(1.5),
    whiteSpace: 'pre-wrap',
  },
}))

export default PzDetailsDialog
